import 'swiper/css';
import 'swiper/css/lazy';
import { gsap } from 'gsap';
import Swiper, { Autoplay, Controller, EffectFade, Lazy, Navigation } from 'swiper';
Swiper.use([Navigation, Autoplay, Controller, EffectFade, Lazy]);
import { useWindowDimensions } from '../utils';
import SwiperProgress from './swiperProgress';
export class MediaAndTextSlider {
    el;
    slider;
    slideProgress;
    swiper;
    textSwiper;
    textSlider;
    constructor(el) {
        this.el = el;
        this.slider = el.querySelector('.swiper.image-swiper');
        this.textSlider = el.querySelector('.swiper.text-swiper');
        const { x } = useWindowDimensions();
        const slideProgress = this.slideProgress;
        const autoplayDelay = 5000;
        const interleaveOffset = 0.5;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const _this = this;
        const options = {
            loop: true,
            speed: 1500,
            watchOverflow: true,
            autoplay: {
                delay: autoplayDelay,
                disableOnInteraction: false,
            },
            breakpoints: {
                768: {
                    speed: 2000,
                    autoplay: {
                        delay: 6000
                    }
                }
            },
            navigation: {
                nextEl: this.el.querySelector('.swiper-button-next'),
                prevEl: this.el.querySelector('.swiper-button-prev'),
            }
        };
        if (this.slider) {
            this.swiper = new Swiper(this.slider, {
                ...options,
                effect: 'slide',
                slidesPerView: 'auto',
                allowTouchMove: false,
                grabCursor: true,
                // Disable preloading of all images
                preloadImages: false,
                lazy: {
                    loadPrevNext: true,
                },
                watchSlidesProgress: true,
                on: {
                    progress: function (el) {
                        for (let i = 0; i < el.slides.length; i++) {
                            const slide = el.slides[i];
                            const slideProgress = slide.progress;
                            const innerOffset = el.width * interleaveOffset;
                            const innerTranslate = slideProgress * innerOffset;
                            slide.querySelector('figure').style.transform = `translate3d(${innerTranslate}px,0, 0)`;
                        }
                    },
                    touchStart: function (el) {
                        for (let i = 0; i < el.slides.length; i++) {
                            const slide = el.slides[i];
                            slide.style.transition = '';
                        }
                    },
                    setTransition: function (el, speed) {
                        for (let i = 0; i < el.slides.length; i++) {
                            const slide = el.slides[i];
                            slide.style.transition = `${speed}ms`;
                            slide.querySelector('figure').style.transition = `${speed}ms`;
                        }
                    },
                    slideChangeTransitionStart: function (swiper) {
                        _this.slideProgress?.progressStop();
                        if (swiper.params.speed) {
                            _this.slideProgress?.progressStart(swiper.params.speed, false);
                        }
                    },
                    slideChangeTransitionEnd: function (swiper) {
                        _this.slideProgress?.progressStop();
                        _this.slideProgress?.progressStart(autoplayDelay, true);
                        for (let i = 0; i < swiper.slides.length; i++) {
                            const slide = swiper.slides[i];
                            if (swiper.activeIndex === i) {
                                const title = slide.querySelector('.media-and-text-slider--title');
                                const subtitle = slide.querySelector('.media-and-text-slider--subtitle');
                                const elements = title.querySelectorAll('span');
                                const tl = gsap.timeline();
                                tl.add('start');
                                tl.fromTo(elements, {
                                    autoAlpha: 0,
                                    transform: 'translate3d(0, 100%, 0)'
                                }, {
                                    duration: 0.75,
                                    transform: 'none',
                                    autoAlpha: 1,
                                    stagger: {
                                        each: 0.06,
                                        from: 'start'
                                    },
                                    delay: 0.125
                                }, 'start');
                                if (subtitle) {
                                    tl.fromTo(subtitle, {
                                        autoAlpha: 0,
                                        y: 16,
                                        transformOrigin: '50% 50%',
                                    }, {
                                        autoAlpha: 1,
                                        y: 0,
                                        transformOrigin: '50% 50%',
                                        duration: 0.25,
                                        delay: 0.5
                                    }, 'start');
                                }
                                tl.add('end');
                                tl.to(elements, {
                                    autoAlpha: 0,
                                    transform: 'translate3d(0, 100%, 0)',
                                    stagger: {
                                        each: 0.06,
                                        from: 'start'
                                    },
                                    delay: swiper.params.speed && swiper.params.speed > 1999 ? 4.6 : 3.6
                                }, 'end');
                                if (subtitle) {
                                    tl.to(subtitle, {
                                        autoAlpha: 0,
                                        y: 16,
                                        transformOrigin: '50% 50%',
                                        delay: swiper.params.speed && swiper.params.speed > 1999 ? 4.6 : 3.6
                                    }, 'end');
                                }
                            }
                        }
                    }
                },
            });
            this.swiper.controller.control = this.textSwiper;
            if (this.el.querySelector('.swiper-slide-progress')) {
                const progressBar = this.el.querySelector('.swiper-slide-progress');
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                this.slideProgress = new SwiperProgress(progressBar);
                this.slideProgress.update(0);
                this.slideProgress.progressStart(autoplayDelay, true);
            }
        }
        if (this.textSlider) {
            this.textSwiper = new Swiper(this.textSlider, {
                ...options,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                slidesPerView: 1,
                on: {
                    slideChangeTransitionStart: function (el) {
                        for (let i = 0; i < el.slides.length; i++) {
                            const slide = el.slides[i];
                            const content = slide.querySelector('.media-and-text-slider--content');
                            if (content) {
                                gsap.to(content, {
                                    autoAlpha: 0,
                                    y: 16,
                                    transformOrigin: '50% 50%',
                                });
                            }
                        }
                    },
                    slideChangeTransitionEnd: function (el) {
                        slideProgress?.progressStop();
                        slideProgress?.progressStart(autoplayDelay, true);
                        for (let i = 0; i < el.slides.length; i++) {
                            const slide = el.slides[i];
                            const content = slide.querySelector('.media-and-text-slider--content');
                            const tl = gsap.timeline();
                            if (content) {
                                tl.fromTo(content, {
                                    autoAlpha: 0,
                                    y: 16,
                                    transformOrigin: '50% 50%',
                                }, {
                                    autoAlpha: 1,
                                    y: 0,
                                    transformOrigin: '50% 50%',
                                    duration: 0.25,
                                    delay: 0.25
                                });
                            }
                        }
                    }
                },
            });
            //this.textSwiper.controller.control =  this.swiper
        }
    }
}
